/**
 * Pure logic for the editor availability screen (spec #297) - kept out of the
 * JSX so it is Vitest-able in under a second, same reasoning as `phases.ts` /
 * `cards.ts` beside the board screen.
 *
 * @module web/screens/availability
 */

export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

export interface DayRange {
  on: boolean;
  fromMinutes: number;
  untilMinutes: number;
}

export type WeekSchedule = Record<Weekday, DayRange>;

export const STEP_MINUTES = 15;
export const DAY_MINUTES = 24 * 60;

/** "09:00" from 540. */
export function fmtMinutes(totalMinutes: number): string {
  const clamped = Math.max(0, Math.min(DAY_MINUTES, Math.round(totalMinutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Snaps a raw minute value onto the nearest 15-minute step, within [0, 1440]. */
export function snapToStep(minutes: number): number {
  const snapped = Math.round(minutes / STEP_MINUTES) * STEP_MINUTES;
  return Math.max(0, Math.min(DAY_MINUTES, snapped));
}

/** A default week - every day off, with a sane 09:00-17:00 range ready for the editor to flip on. */
export function defaultWeek(): WeekSchedule {
  return Object.fromEntries(WEEKDAYS.map((d) => [d, { on: false, fromMinutes: 9 * 60, untilMinutes: 17 * 60 }])) as WeekSchedule;
}

/**
 * The first problem with this week, or null when it is valid - mirrors the
 * server's own `dispatch-rules.ts#validateWeeklyAvailability` so the client
 * can block Save before round-tripping, without re-deriving a second copy of
 * the actual rule (the server validates again regardless).
 */
export function validateWeek(days: WeekSchedule): string | null {
  for (const day of WEEKDAYS) {
    const d = days[day];
    if (!d.on) continue;
    if (d.fromMinutes >= d.untilMinutes) return `${WEEKDAY_LABELS[day]}: from must be before until`;
  }
  return null;
}
